'use client';

import type { UseFormRegister } from 'react-hook-form';

type Props = {
  name: string;
  label: string;
  register: UseFormRegister<any>;
  error?: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  required?: boolean;
};

/**
 * Boolean-only radio group (used for `tieneCuenta` in Plan 25-03).
 * `setValueAs: (v) => v === 'true'` coerces the HTML radio's string `"true"`/`"false"`
 * to a real boolean before yup sees it (RESEARCH.md Pitfall 2).
 *
 * If a non-boolean radio is needed in the future, fork into a generic variant
 * instead of stripping the coercion in-place.
 */
export function RadioGroupField({ name, label, register, error, options, required }: Props) {
  const errorId = `field-${name}-error`;

  return (
    <fieldset aria-invalid={!!error} aria-describedby={error ? errorId : undefined}>
      <legend className="block text-sm font-semibold text-[#373A36] mb-2">
        {label}
        {required && <span className="text-red-500 font-bold ml-0.5">*</span>}
      </legend>
      <div className="space-y-2">
        {options.map((opt) => (
          <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              value={opt.value}
              className="w-4 h-4 text-[#00A19B] focus:ring-2 focus:ring-[#00A19B]/20"
              {...register(name, { setValueAs: (v: string) => v === 'true' })}
            />
            <span className="text-sm text-[#373A36]">{opt.label}</span>
          </label>
        ))}
      </div>
      {error && (
        <p id={errorId} className="mt-1 text-xs text-red-500">
          {error}
        </p>
      )}
    </fieldset>
  );
}
